import FuseUtils from '@fuse/utils/FuseUtils';
import axios from 'axios';
import {
  isTransientOutageError,
  normalizeApiError,
} from 'app/utils/apiError';
import jwtServiceConfig from './jwtServiceConfig';

/* eslint-disable camelcase */

class JwtService extends FuseUtils.EventEmitter {
  init() {
    this.setInterceptors();
    this.handleAuthentication();
  }

  setInterceptors = () => {
    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (err) => {
        if (err.response?.status === 401 && err.config && !err.config.__isRetryRequest) {
          this.emit('onAutoLogout', 'Session expired');
          this.setSession(null);
        }

        return Promise.reject(normalizeApiError(err));
      }
    );
  };

  handleAuthentication = () => {
    const access_token = this.getAccessToken();

    if (!access_token) {
      this.emit('onNoAccessToken');

      return;
    }
    if (this.isAuthTokenValid(access_token)) {
      this.setSession(access_token);
      this.emit('onAutoLogin', true);
    } else {
      this.setSession(null);
      this.emit('onAutoLogout', 'Session expired');
    }
  };

  createUser = (data) => {
    return new Promise((resolve, reject) => {
      axios.post(jwtServiceConfig.signUp, data).then((response) => {
        if (response.data.user) {
          this.setSession(response.data.access_token);
          resolve(response.data.user);
          this.emit('onLogin', response.data.user);
        } else {
          reject(response.data.error);
        }
      });
    });
  };

  signInWithEmailAndPassword = (email, password, rememberMe) => {
    return new Promise((resolve, reject) => {
      axios
        .post(jwtServiceConfig.signIn, {
          email,
          password,
          rememberMe,
        })
        .then((response) => {
          if (response.data.user) {
            this.setVerify(response.data);
            this.setSession(response.data.access_token);
            resolve(response.data.user);
            this.emit('onLogin', response.data.user);
          } else {
            reject(response.data.error);
          }
        })
        .catch((error) => {
          reject(normalizeApiError(error));
        });
    });
  };

  signInWithToken = () => {
    return new Promise((resolve, reject) => {
      axios
        .get(jwtServiceConfig.getUser)
        .then((response) => {
          if (response.data.user) {
            /**
             * Token-based login does not return verification info(verify field).
             * If the token is valid, assume the user is already verified.
             * This prevents redirecting verified users to the verify page
             * (e.g. when accessing /sign-in with an existing session).
             *
             * NOTE:
             * - When a user is logged in but verification is still pending,
             *   this API responds with 401 (Unauthorized).
             * - Frontend treats 401 as an invalid session and forces logout.
             * - This prevents unverified users from accessing protected routes.
             */
            response.data.user.verify = false;
            response.data.user.isVerified = true;
            resolve(response.data.user);
          } else {
            this.logout();
            reject(new Error('Session expired.'));
          }
        })
        .catch((error) => {
          // Keep the stored token on a brief API outage (restart, 502/503/504,
          // dropped TCP). Clearing it sends everyone to sign-in and they all
          // log in at once when the process is back — that wave OOMs prod
          // (PGSS-5337). Real 401 still logs out via the interceptor.
          if (!isTransientOutageError(error)) {
            this.logout();
          }
          // Keep response/status so AuthContext can still classify a 503.
          // `new Error(message)` dropped that and sent everyone to sign-in.
          reject(error);
        });
    });
  };

  updateUserData = (user) => {
    return axios.post(jwtServiceConfig.updateUser, {
      user,
    });
  };

  setVerify = (data) => {
    if (data.verify) {
      localStorage.setItem('verify_pending', data.verify);
      data.user.verify = data.verify;
      data.user.loginRedirectUrl = '/verify';
      data.user.isVerified = false;
    } else {
      data.user.verify = false;
      data.user.isVerified = true;
      localStorage.removeItem('verify_pending');
    }
  };

  setSession = (access_token) => {
    if (access_token) {
      localStorage.setItem('jwt_access_token', access_token);
      axios.defaults.headers.common.Authorization = `Bearer ${access_token}`;
    } else {
      localStorage.removeItem('jwt_access_token');
      delete axios.defaults.headers.common.Authorization;
    }
  };

  logout = () => {
    axios
      .get(jwtServiceConfig.logout)
      .then((response) => {
        this.setSession(null);
        this.emit('onLogout', 'Logged out');
      })
      .catch((error) => {
        this.setSession(null);
      });
  };

  isAuthTokenValid = (access_token) => {
    if (!access_token) {
      return false;
    }
    // const decoded = jwtDecode(access_token);
    // const currentTime = Date.now() / 1000;
    // if (decoded.exp < currentTime) {
    // console.warn('session expired');
    // return false;
    // }

    return true;
  };

  getAccessToken = () => {
    return window.localStorage.getItem('jwt_access_token');
  };
}

const instance = new JwtService();

export default instance;
